import React, { useMemo, useState } from 'react';
import { ActivityIndicator, Text, TouchableOpacity, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { LegendList } from '@legendapp/list';
import { DateTime } from 'luxon';
import { useRouter } from 'expo-router';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import SolidButton from '@/components/General/SolidButton';
import SegmentedTabs from '@/components/General/SegmentedTabs';
import TaskCard from '@/components/Tasks/TaskCard';
import DropdownMenu from '@/components/General/DropdownMenu';
import SearchBox from '@/components/General/inputs/SearchBox';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import Unicon from '@/components/General/Unicon';
import { Query } from '@/utils/react-query/Queries';
import { isAutomationDueType, isFollowUpDueType } from '@/utils/constants/taskTypes';

const TASK_FILTERS = [
    { label: "Active & Upcoming", value: "active" },
    { label: "Past & Completed", value: "history" },
];

const STATUS_OPTIONS = [
    { label: "All statuses", value: "all" },
    { label: "Pending", value: "pending" },
    { label: "Awaiting response", value: "awaiting" },
    { label: "Accepted", value: "accepted" },
    { label: "Declined", value: "declined" },
    { label: "Completed", value: "completed" },
    { label: "Cancelled", value: "cancelled" },
    { label: "Overdue", value: "overdue" },
];

const DATE_PRESETS = [
    { label: "All dates", value: "all" },
    { label: "Next 7 days", value: "next7" },
    { label: "Next 30 days", value: "next30" },
    { label: "Next 90 days", value: "next90" },
    { label: "Past", value: "past" },
];

const getTaskDueDate = (taskInstance) => {
    const value = taskInstance?.dueDate || taskInstance?.EventInstance?.startTime;
    if (!value) return null;

    const date = DateTime.fromISO(value);
    return date.isValid ? date : null;
};

const isTaskHistory = (taskInstance) => {
    if (taskInstance?.completed) return true;
    // Cancelled work is history, never "active & upcoming" — but it must not
    // be presented as completed either.
    if (taskInstance?.cancelledAt) return true;
    // Automation/Follow Up tasks only leave the active list when completed or
    // deleted — a lapsed dueDate is a reassignment timeout, not an assumed
    // completion (spec §10.7).
    if (isAutomationDueType(taskInstance?.Task?.dueType) || isFollowUpDueType(taskInstance?.Task?.dueType)) return false;
    const dueDate = getTaskDueDate(taskInstance);
    return Boolean(taskInstance?.Task?.assumeCompleted && dueDate && dueDate < DateTime.local());
};

const getTaskDateLabel = (date) => {
    if (!date) return "No due date";

    const today = DateTime.local().startOf("day");
    if (date.hasSame(today, "day")) return `Today · ${date.toLocaleString(DateTime.DATE_MED)}`;
    if (date.hasSame(today.plus({ days: 1 }), "day")) return `Tomorrow · ${date.toLocaleString(DateTime.DATE_MED)}`;
    return date.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY);
};

// Status buckets for the status filter. Completed and overdue are row states;
// the rest derive from the profile person's own assignment row.
const getTaskStatus = (taskInstance, personID) => {
    if (taskInstance?.cancelledAt) return "cancelled";
    if (taskInstance?.completed) return "completed";
    const dueDate = getTaskDueDate(taskInstance);
    if (!taskInstance?.Task?.assumeCompleted && dueDate && dueDate < DateTime.local()) return "overdue";
    const myAssignment = taskInstance?.Assignees?.find(a => a?.id === personID || a?.personID === personID);
    if (myAssignment?.assignmentResponse === "ACCEPTED") return "accepted";
    if (myAssignment?.assignmentResponse === "DECLINED") return "declined";
    if (myAssignment) return "awaiting";
    return "pending";
};

const taskMatchesSearch = (taskInstance, lower) => {
    const dueDate = getTaskDueDate(taskInstance);
    return [
        taskInstance?.Task?.title,
        taskInstance?.Task?.description,
        taskInstance?.additionalDetails,
        taskInstance?.EventInstance?.Event?.name,
        taskInstance?.EventInstance?.nameOverride,
        taskInstance?.VolunteerTeam?.name,
        dueDate?.toLocaleString(DateTime.DATE_MED),
        dueDate?.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY),
        dueDate?.toISODate(),
    ].some(h => h && h.toLowerCase().includes(lower));
};

const taskMatchesDatePreset = (taskInstance, preset) => {
    if (preset === "all") return true;
    const dueDate = getTaskDueDate(taskInstance);
    if (!dueDate) return false;

    const today = DateTime.local().startOf("day");
    if (preset === "past") return dueDate < today;
    const days = { next7: 7, next30: 30, next90: 90 }[preset];
    if (!days) return true;
    return dueDate >= today && dueDate <= today.plus({ days });
};

const PersonTasksTab = ({ tasks = [], personID, fromScreen = `/people/${personID}` }) => {
    const router = useRouter();
    const [taskFilter, setTaskFilter] = useState("active");
    const [searchText, setSearchText] = useState("");
    const [teamFilter, setTeamFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const [eventFilter, setEventFilter] = useState(null);
    const [dateFilter, setDateFilter] = useState("all");

    const showHistory = taskFilter === "history";
    const historyQuery = Query.tasks.personHistory({
        personID,
        options: { enabled: showHistory && !!personID },
    });
    const { data: allTeams = [] } = Query.taskTeams.all();
    const { data: loggedInUser } = Query.people.loggedInUser();

    // Care tasks link to the person through FollowUpTaskContexts, not the
    // instance's assignee rows — merge them for Follow Up team viewers only
    // (spec §8.4: non-team viewers never browse another person's care work).
    const { data: careTasks = [] } = Query.followUp.personTasks({
        organizationID: loggedInUser?.organizationID,
        personID,
        options: { enabled: !!personID && !!loggedInUser?.can?.('View Follow Up') },
    });

    const mergedTasks = useMemo(() => {
        const seen = new Set((tasks || []).map(t => t.id));
        return [...(tasks || []), ...careTasks.filter(t => !seen.has(t.id))];
    }, [tasks, careTasks]);

    const activeTasks = useMemo(() => mergedTasks.filter(task => !isTaskHistory(task)), [mergedTasks]);
    const historyTasks = historyQuery.data;
    const mergedHistory = useMemo(() => {
        const seen = new Set((historyTasks || []).map(t => t.id));
        return [...(historyTasks || []), ...careTasks.filter(t => isTaskHistory(t) && !seen.has(t.id))];
    }, [historyTasks, careTasks]);
    const visibleTasks = showHistory ? mergedHistory : activeTasks;

    // Teams the profile person belongs to (assigner or role member). Fallback:
    // any team appearing on their tasks, so assignments via other paths still
    // surface in the filter.
    const personTeams = useMemo(() => {
        const map = new Map();
        allTeams.forEach(team => {
            if (team.Assigners?.some(a => a.id === personID) || team.Members?.some(m => m.id === personID)) {
                map.set(team.id, team);
            }
        });
        [...tasks, ...(historyTasks || [])].forEach(task => {
            if (task?.VolunteerTeam?.id && !map.has(task.VolunteerTeam.id)) {
                map.set(task.VolunteerTeam.id, task.VolunteerTeam);
            }
        });
        return Array.from(map.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [allTeams, tasks, historyTasks, personID]);

    // Event rows embed only `name` (People/personHistory selects), so events
    // are keyed by name rather than id.
    const eventOptions = useMemo(() => {
        const names = new Set();
        [...tasks, ...(historyTasks || [])].forEach(task => {
            const name = task?.EventInstance?.Event?.name;
            if (name) names.add(name);
        });
        return Array.from(names).sort((a, b) => a.localeCompare(b));
    }, [tasks, historyTasks]);

    const filteredTasks = useMemo(() => {
        const lower = searchText.trim().toLowerCase();
        return visibleTasks.filter(task => {
            if (teamFilter && task?.VolunteerTeam?.id !== teamFilter.id && task?.volunteerTeamID !== teamFilter.id) return false;
            if (eventFilter && task?.EventInstance?.Event?.name !== eventFilter) return false;
            if (statusFilter !== "all" && getTaskStatus(task, personID) !== statusFilter) return false;
            if (!taskMatchesDatePreset(task, dateFilter)) return false;
            if (lower && !taskMatchesSearch(task, lower)) return false;
            return true;
        });
    }, [visibleTasks, teamFilter, eventFilter, statusFilter, dateFilter, searchText, personID]);

    const taskRows = useMemo(() => {
        const sortedTasks = [...filteredTasks].sort((a, b) => {
            const aDate = getTaskDueDate(a)?.toMillis();
            const bDate = getTaskDueDate(b)?.toMillis();
            if (aDate == null && bDate == null) return 0;
            if (aDate == null) return 1;
            if (bDate == null) return -1;
            return showHistory ? bDate - aDate : aDate - bDate;
        });

        const groups = new Map();
        sortedTasks.forEach(task => {
            const dueDate = getTaskDueDate(task);
            const key = dueDate?.toISODate() || "no-due-date";
            if (!groups.has(key)) groups.set(key, { dueDate, tasks: [] });
            groups.get(key).tasks.push(task);
        });

        return Array.from(groups.entries()).flatMap(([key, group]) => [
            { type: "header", id: `task-date-${key}`, label: getTaskDateLabel(group.dueDate) },
            ...group.tasks.map(task => ({ type: "task", id: task.id, task })),
        ]);
    }, [showHistory, filteredTasks]);

    const visibleLabel = showHistory ? "past / completed" : "active / upcoming";
    const filterItems = TASK_FILTERS.map(item => ({
        ...item,
        label: item.value === "active" ? `${item.label} (${activeTasks.length})` : item.label,
    }));

    const hasActiveFilters = Boolean(searchText.trim() || teamFilter || eventFilter || statusFilter !== "all" || dateFilter !== "all");
    const clearFilters = () => {
        setSearchText("");
        setTeamFilter(null);
        setStatusFilter("all");
        setEventFilter(null);
        setDateFilter("all");
    };

    const filterChip = (icon, label, active) => (
        <View style={[styles.filterChip, active && styles.filterChipActive]}>
            <Unicon name={icon} size={14} colorVariant={active ? "tasks" : "textSecondary"} />
            <Text style={[styles.filterChipText, active && styles.filterChipTextActive]} numberOfLines={1}>{label}</Text>
            <Unicon name="chevron-down" size={12} colorVariant={active ? "tasks" : "textSecondary"} />
        </View>
    );

    const teamQueryResult = useMemo(() => ({ data: personTeams }), [personTeams]);

    return (
        <View style={styles.container}>
            <View style={styles.filterPanel}>
                <View style={styles.topRow}>
                    <View style={styles.tabsWrap}>
                        <View style={styles.tabsInner}>
                            <SegmentedTabs
                                tabs={filterItems.map(item => ({ key: item.value, label: item.label }))}
                                value={taskFilter}
                                onChange={setTaskFilter}
                                testIDPrefix="person-tasks-filter"
                            />
                        </View>
                    </View>
                    <TouchableOpacity testID="person-tasks-dashboard" accessibilityRole="button" accessibilityLabel="Open tasks dashboard" style={styles.dashboardLink} onPress={() => router.push('/tasks')}>
                        <Text style={styles.dashboardLinkText}>Tasks Dashboard</Text>
                        <Unicon name="arrow-forward" size={14} colorVariant="tasks" />
                    </TouchableOpacity>
                </View>

                <View style={styles.searchWrap}>
                    <SearchBox testID="person-tasks-search" searchText={searchText} onChangeText={setSearchText} placeholder="Search tasks..." />
                </View>

                <View style={styles.filtersRow}>
                    {personTeams.length > 0 && (
                        <SheetSelector
                            type="taskTeams"
                            title="Filter by Team"
                            queryResult={teamQueryResult}
                            value={teamFilter}
                            onChange={setTeamFilter}
                        >
                            {filterChip("people-outline", teamFilter?.name || 'Team', !!teamFilter)}
                        </SheetSelector>
                    )}
                    <DropdownMenu
                        testID="person-tasks-status-filter"
                        title="Status"
                        options={STATUS_OPTIONS.map(option => ({
                            name: option.label,
                            selected: statusFilter === option.value,
                            onSelect: () => setStatusFilter(option.value),
                        }))}
                        triggerComponent={filterChip("flag-outline", STATUS_OPTIONS.find(o => o.value === statusFilter)?.label || 'Status', statusFilter !== "all")}
                    />
                    {eventOptions.length > 0 && (
                        <DropdownMenu
                            testID="person-tasks-event-filter"
                            title="Event"
                            showSearch
                            options={[
                                { name: "All events", selected: !eventFilter, onSelect: () => setEventFilter(null) },
                                ...eventOptions.map(name => ({
                                    name,
                                    selected: eventFilter === name,
                                    onSelect: () => setEventFilter(name),
                                })),
                            ]}
                            triggerComponent={filterChip("calendar-outline", eventFilter || 'Event', !!eventFilter)}
                        />
                    )}
                    <DropdownMenu
                        testID="person-tasks-date-filter"
                        title="Date"
                        options={DATE_PRESETS.map(option => ({
                            name: option.label,
                            selected: dateFilter === option.value,
                            onSelect: () => setDateFilter(option.value),
                        }))}
                        triggerComponent={filterChip("time-outline", DATE_PRESETS.find(o => o.value === dateFilter)?.label || 'Date', dateFilter !== "all")}
                    />
                    {hasActiveFilters && (
                        <TouchableOpacity testID="person-tasks-clear-filters" accessibilityRole="button" accessibilityLabel="Clear task filters" style={styles.clearFilters} onPress={clearFilters}>
                            <Unicon name="close" size={13} colorVariant="tasks" />
                            <Text style={styles.clearFiltersText}>Clear filters</Text>
                        </TouchableOpacity>
                    )}
                </View>
            </View>
            {showHistory && historyQuery.isLoading ? (
                <View style={styles.status} testID="person-task-history-loading">
                    <ActivityIndicator />
                    <Text style={styles.statusText}>Loading task history...</Text>
                </View>
            ) : showHistory && historyQuery.isError ? (
                <View style={styles.status} testID="person-task-history-error">
                    <Text style={styles.statusText}>Could not load task history.</Text>
                    <SolidButton text="Try again" onPress={() => historyQuery.refetch()} width={100} />
                </View>
            ) : (
                <LegendList
                    recycleItems
                    keyExtractor={(item) => item.id}
                    getItemType={(item) => item.type}
                    estimatedItemSize={100}
                    data={taskRows}
                    renderItem={({ item }) => item.type === "header" ? (
                        <View style={styles.dateHeader} testID={item.id}>
                            <Text style={styles.dateHeaderText}>{item.label}</Text>
                        </View>
                    ) : (
                        <TaskCard taskInstance={item.task} showEvent personID={personID} fromScreen={fromScreen} />
                    )}
                    ListEmptyComponent={<EmptyListPlaceholder message={hasActiveFilters ? "No tasks match the current filters" : `No ${visibleLabel} tasks`} />}
                    contentContainerStyle={styles.listContent}
                    keyboardShouldPersistTaps="handled"
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create((theme) => ({
    container: { flex: 1 },
    filterPanel: { paddingHorizontal: 20, paddingTop: 16, paddingBottom: 10, backgroundColor: theme.colors.background, gap: 12 },
    topRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 12 },
    tabsWrap: { flex: 1, minWidth: 260, alignItems: 'center' },
    tabsInner: { width: '100%', maxWidth: 550 },
    dashboardLink: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 5, flexShrink: 0 },
    dashboardLinkText: { color: theme.colors.tasks, fontFamily: theme.fonts.bold, fontSize: 12 },
    searchWrap: { width: '100%' },
    filtersRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
    filterChip: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 10, paddingVertical: 7, borderRadius: 16, borderWidth: 1, borderColor: theme.colors.border, backgroundColor: theme.colors.surface },
    filterChipActive: { borderColor: theme.colors.tasks, backgroundColor: theme.colors.tasksContainer },
    filterChipText: { fontSize: 12, fontFamily: theme.fonts.body, color: theme.colors.textSecondary, maxWidth: 140 },
    filterChipTextActive: { color: theme.colors.tasks, fontFamily: theme.fonts.bold },
    clearFilters: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 7, paddingHorizontal: 4 },
    clearFiltersText: { fontSize: 12, fontFamily: theme.fonts.bold, color: theme.colors.tasks },
    status: { alignItems: 'center', justifyContent: 'center', gap: 12, paddingVertical: 64, paddingHorizontal: 16 },
    statusText: { fontSize: 14, color: theme.colors.textSecondary, fontFamily: theme.fonts.medium, textAlign: 'center' },
    listContent: { paddingHorizontal: 20, paddingTop: 4, paddingBottom: 100 },
    dateHeader: { flexDirection: 'row', alignItems: 'center', paddingTop: 12, paddingBottom: 8, paddingHorizontal: 4 },
    dateHeaderText: { fontSize: 13, fontFamily: theme.fonts.bold, color: theme.colors.text, textTransform: 'uppercase', letterSpacing: 0.4 },
}));

export default PersonTasksTab;
